import React, { useState, useRef } from 'react'
import ReactCrop, { type Crop, type PixelCrop } from 'react-image-crop'
import 'react-image-crop/dist/ReactCrop.css'
import Dialog from '@mui/material/Dialog'
import DialogTitle from '@mui/material/DialogTitle'
import DialogContent from '@mui/material/DialogContent'
import DialogActions from '@mui/material/DialogActions'
import Button from '@mui/material/Button'
import Box from '@mui/material/Box'
import CircularProgress from '@mui/material/CircularProgress'
import Alert from '@mui/material/Alert'
import { Vehicle } from '../../../shared/types/vehicle.types'
import useFile from '../wrappers/use-file'
import useVehicle from '../wrappers/use-vehicle'
import { useTranslation } from 'react-i18next'
import {
    vehiclePictureUploadDialogStyles,
    vehiclePictureUploadPreviewImageStyle,
} from './VehiclePictureUploadDialog.styles'

function getCroppedImg(image: HTMLImageElement, pixelCrop: PixelCrop, originalName: string): Promise<File> {
    const scaleX = image.naturalWidth / image.width
    const scaleY = image.naturalHeight / image.height
    const canvas = document.createElement('canvas')
    canvas.width = pixelCrop.width * scaleX
    canvas.height = pixelCrop.height * scaleY
    const ctx = canvas.getContext('2d')!
    ctx.drawImage(
        image,
        pixelCrop.x * scaleX, pixelCrop.y * scaleY,
        pixelCrop.width * scaleX, pixelCrop.height * scaleY,
        0, 0,
        canvas.width, canvas.height,
    )

    return new Promise((resolve, reject) => {
        canvas.toBlob(blob => {
            if (!blob) { reject(new Error('Canvas is empty')); return }
            resolve(new File([blob], originalName, { type: 'image/jpeg' }))
        }, 'image/jpeg', 0.9)
    })
}

interface Props {
    open: boolean
    vehicle: Vehicle
    onClose: () => void
    onSuccess: (url: string) => void
}

const VehiclePictureUploadDialog: React.FC<Props> = ({ open, vehicle, onClose, onSuccess }) => {
    const { t } = useTranslation()
    const { create: createFile } = useFile()
    const { edit: editVehicle } = useVehicle()
    const imgRef = useRef<HTMLImageElement>(null)

    const [imageSrc, setImageSrc] = useState<string | null>(null)
    const [originalFile, setOriginalFile] = useState<File | null>(null)
    const [crop, setCrop] = useState<Crop>()
    const [completedCrop, setCompletedCrop] = useState<PixelCrop>()
    const [error, setError] = useState<string | null>(null)
    const [uploading, setUploading] = useState(false)

    const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0]
        if (!file) return
        if (!file.type.startsWith('image/')) {
            setError(t('vehiclePictureUpload.notImageError'))
            return
        }
        setError(null)
        setOriginalFile(file)
        setCrop(undefined)
        setCompletedCrop(undefined)
        const reader = new FileReader()
        reader.onload = () => setImageSrc(reader.result as string)
        reader.readAsDataURL(file)
    }

    const handleUpload = async () => {
        if (!imgRef.current || !originalFile) return
        setUploading(true)
        try {
            let fileToUpload: File
            if (completedCrop && completedCrop.width > 0 && completedCrop.height > 0) {
                fileToUpload = await getCroppedImg(imgRef.current, completedCrop, originalFile.name)
            } else {
                fileToUpload = originalFile
            }
            const fileDoc = await createFile(fileToUpload, { vehicleUid: vehicle.uid, garageUid: vehicle.garageUid })
            await editVehicle(vehicle.uid, { pictureUrl: fileDoc.url })
            onSuccess(fileDoc.url)
            handleClose()
        } catch {
            setError(t('vehiclePictureUpload.uploadError'))
        } finally {
            setUploading(false)
        }
    }

    const handleClose = () => {
        setImageSrc(null)
        setOriginalFile(null)
        setCrop(undefined)
        setCompletedCrop(undefined)
        setError(null)
        onClose()
    }

    return (
        <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
            <DialogTitle>{t('vehiclePictureUpload.title')}</DialogTitle>
            <DialogContent>
                <Box sx={vehiclePictureUploadDialogStyles.content}>
                    {error && <Alert severity="error">{error}</Alert>}
                    <Button variant="outlined" component="label">
                        {t('vehiclePictureUpload.selectButton')}
                        <input type="file" accept="image/*" hidden onChange={handleFileChange} />
                    </Button>
                    {imageSrc && (
                        <Box sx={vehiclePictureUploadDialogStyles.previewContainer}>
                            <ReactCrop
                                crop={crop}
                                onChange={c => setCrop(c)}
                                onComplete={c => setCompletedCrop(c)}
                            >
                                <img
                                    ref={imgRef}
                                    src={imageSrc}
                                    alt={t('vehiclePictureUpload.previewAlt')}
                                    style={vehiclePictureUploadPreviewImageStyle}
                                />
                            </ReactCrop>
                        </Box>
                    )}
                </Box>
            </DialogContent>
            <DialogActions>
                <Button onClick={handleClose} disabled={uploading}>{t('common.cancel')}</Button>
                <Button
                    variant="contained"
                    onClick={handleUpload}
                    disabled={!imageSrc || uploading}
                    startIcon={uploading ? <CircularProgress size={16} /> : undefined}
                >
                    {uploading ? t('vehiclePictureUpload.uploading') : t('vehiclePictureUpload.upload')}
                </Button>
            </DialogActions>
        </Dialog>
    )
}

export default VehiclePictureUploadDialog
